<template>
    <div class="test-run-index">
        <b-loading is-full-page :active="isLoading"></b-loading>
        <b-notification v-if="error" type="is-danger is-light" aria-close-label="Close notification" role="alert">{{ error }}</b-notification>
        <div class="card">
            <div class="card-content">
                <div class="columns is-tablet">
                    <div class="column">
                        <p class="title is-5">Target filters</p>
                        <b-field label="Product">
                            <b-taginput
                                v-model="selectedProducts"
                                :data="filteredProducts"
                                autocomplete
                                open-on-focus
                                icon="label"
                                field="name"
                                placeholder="Add a product"
                                @typing="queries.product = $event">
                            </b-taginput>
                        </b-field>
                        <b-field label="Branch">
                            <b-taginput
                                v-model="selectedBranches"
                                :data="filteredBranches"
                                autocomplete
                                open-on-focus
                                icon="label"
                                field="name"
                                placeholder="Add a branch"
                                @typing="queries.branch = $event">
                            </b-taginput>
                        </b-field>
                        <div class="field">
                            <b-checkbox v-model="filters.only_last_commits">
                                Latest commits only
                            </b-checkbox>
                        </div>
                        <b-field label="Commit id">
                            <b-input v-model="filters.commit_id"/>
                        </b-field>
                        <b-field label="Columnstore commit id">
                            <b-input v-model="filters.columnstore_commit_id"/>
                        </b-field>
                        <b-field label="Target (regexp)">
                            <b-input
                                v-model="filters.target_name"
                                :disabled="filters.only_last_commits"
                                placeholder="Multiple targets: target_1|target_2"/>
                        </b-field>
                        <b-tag v-if="filters.excluded_target_build_ids.length > 0"
                               type="is-primary"
                               closable
                               aria-close-label="Clear"
                               @close="clearExcludedTargetBuilds">
                            {{ filters.excluded_target_build_ids.length }} excluded target builds
                        </b-tag>
                    </div>
                    <div class="column">
                        <p class="title is-5">Test run filters</p>
                        <b-field label="Platform">
                            <b-taginput
                                v-model="selectedPlatforms"
                                :data="filteredPlatforms"
                                autocomplete
                                open-on-focus
                                icon="label"
                                field="name"
                                placeholder="Add a platform"
                                @typing="queries.platform = $event">
                            </b-taginput>
                        </b-field>
                        <b-field label="CI service">
                            <b-taginput
                                v-model="selectedCIServices"
                                :data="filteredCIServices"
                                autocomplete
                                open-on-focus
                                icon="label"
                                field="name"
                                placeholder="Add a CI service"
                                @typing="queries.ciService = $event">
                            </b-taginput>
                        </b-field>
                        <b-field label="Build trigger">
                            <b-taginput
                                v-model="selectedBuildTypes"
                                :data="filteredBuildTypes"
                                autocomplete
                                open-on-focus
                                icon="label"
                                field="name"
                                placeholder="Add a build trigger"
                                @typing="queries.buildType = $event">
                            </b-taginput>
                        </b-field>
                        <b-field label="MTR">
                            <b-taginput
                                v-model="selectedMtrNames"
                                :data="filteredMtrNames"
                                autocomplete
                                open-on-focus
                                icon="label"
                                field="name"
                                placeholder="Add a MTR"
                                @typing="queries.mtrName = $event">
                            </b-taginput>
                        </b-field>
                        <b-field>
                            <template #label>
                                Run id
                                <b-tooltip
                                    type="is-dark"
                                    label="CI service run id (e.g. BuildBot buildId)">
                                    <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                                </b-tooltip>
                            </template>
                            <b-input v-model="filters.run_id"/>
                        </b-field>
                        <b-switch v-model="filters.runs_with_additional_tools">
                            <template v-if="filters.runs_with_additional_tools">
                                Show runs with additional tools only
                            </template>
                            <template v-else>
                                Hide runs with additional tools
                            </template>
                        </b-switch>
                        <b-field label="Additional tools" v-if="filters.runs_with_additional_tools">
                            <b-taginput
                                v-model="selectedAdditionalTools"
                                :data="filteredAdditionalTools"
                                autocomplete
                                open-on-focus
                                icon="label"
                                field="name"
                                placeholder="Add an additional tool"
                                @typing="queries.additionalTool = $event">
                            </b-taginput>
                        </b-field>
                    </div>
                    <div class="column">
                        <p class="title is-5">Test filters</p>
                        <b-field label="Test suite">
                            <b-taginput
                                v-model="selectedTestSuiteNames"
                                :data="filteredTestSuiteNames"
                                autocomplete
                                open-on-focus
                                icon="label"
                                field="name"
                                placeholder="Add a test suite"
                                @typing="queries.testSuiteName = $event">
                            </b-taginput>
                        </b-field>
                        <b-field label="Test case name (regexp)">
                            <b-input v-model="filters.test_name"/>
                        </b-field>
                        <b-field label="Time interval">
                            <div class="field">
                                <b-select expanded v-model="filters.time_interval"
                                          :disabled="filters.only_last_commits">
                                    <option
                                        v-for="option in TIME_INTERVAL_OPTIONS"
                                        :value="option.value.toString()"
                                        :key="option.value">
                                        {{ option.title }}
                                    </option>
                                </b-select>
                                <b-datepicker
                                        :disabled="filters.only_last_commits"
                                        v-if="filters.time_interval === '-2'"
                                        placeholder="Click to select..."
                                        v-model="filters.dates"
                                        icon="calendar-today"
                                        range>
                                </b-datepicker>
                            </div>
                        </b-field>
                    </div>
                </div>
                <b-button type="is-primary" @click="onSearchClick">Search</b-button>
            </div>
        </div>
        <br>

        <template v-if="dataIsLoaded">
            <p>
                Found <b>{{ testRunsTargetBuildsTotalCount }} target builds</b>
                ({{ Math.ceil(testRunsTargetBuildsTotalCount/PER_PAGE) }} pages)
            </p>
        </template>

        <br>
        <b-pagination
                id="tablePagination"
                v-if="dataIsLoaded"
                style="width: 20vw;"
                :total="testRunsTargetBuildsTotalCount"
                :current="currentPage"
                size="is-small"
                v-on:change="onChangePage"
                :per-page="PER_PAGE"
                order="is-centered"
                aria-next-label="Next page"
                aria-previous-label="Previous page"
                aria-page-label="Page"
                aria-current-label="Current page">
        </b-pagination>
        <test-cases-table :test-cases="testCasesPage"
                          :test-results="testResults"
                          :target-builds="testRunsTargetBuilds"
                          v-on:select-test-result="onSelectTestResult"
                          v-on:select-target-build="onSelectTargetBuild"
                          @delete-target-build="excludeTargetBuild"
                          :key="rowsCurrentPage"
                          :fetch-jira-issues="fetchJiraIssues"
                          :fetch-jira-summary="fetchJiraSummary"
                          v-if="!isLoading">
            <template #empty-message>
                <span v-if="!dataIsLoaded">
                    Set filters and click "Search"
                </span>
                <span v-else>
                    No results found
                </span>
            </template>
        </test-cases-table>
        <div class="container" style="margin-bottom: 1vh" v-if="dataIsLoaded && !isLoading">
          <div class="level">
            <b-button active style="width: 25vw" icon-left="arrow-up" :disabled="rowsCurrentPage <= 1" @click="prevCasesPage">
              Show previous test cases page{{ this.rowsCurrentPage <= 1 ? "" : ": " + (this.rowsCurrentPage - 1) }}
            </b-button>
            <p>
              <b> Current test cases page: {{ this.rowsCurrentPage }} / {{ this.totalCasesPages }} </b>
            </p>
            <b-button active style="width: 25vw" icon-left="arrow-down" :disabled="rowsCurrentPage >= totalCasesPages" @click="nextCasesPage">
              Show next test cases page{{ this.rowsCurrentPage >= this.totalCasesPages ? "" : ": " + (this.rowsCurrentPage + 1) }}
            </b-button>
          </div>
        </div>

        <b-modal :active="currentTestResultsDetails != null"
                 full-screen has-modal-card :can-cancel="false">
            <div class="modal-card" style="width: auto">
                <header class="modal-card-head">
                    <p class="modal-card-title">Test results</p>
                    <button class="delete" aria-label="close" @click="onCloseTestResultsDetails()"></button>
                </header>
                <div class="modal-card-body">
                    <test-results-details v-if="currentTestResultsDetails"
                                          :target-build-id="currentTestResultsDetails.targetBuildId"
                                          :test-case-id="currentTestResultsDetails.testCaseId"
                                          :test-and-run-filters="currentTestResultsDetails.testAndRunFilters"/>
                </div>

            </div>
        </b-modal>

        <b-modal :active="currentTargetBuildDetails != null" :width="600" @close="onCloseTargetBuild">
            <div class="card">
                <div class="card-content">
                    <target-build-show v-if="currentTargetBuildDetails && testRunsTargetBuilds.length > 0"
                                       @close="onCloseTargetBuild"
                                       :target-build-id="currentTargetBuildDetails"
                                       :dates="filters.dates"
                                       :time-interval="filters.time_interval"/>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
    import {mapActions, mapGetters, mapMutations} from 'vuex';
    import TestCasesTable from "../components/TestCasesTable";
    import TestResultsDetails from "./TestResultsDetails";
    import TargetBuildShow from "./TargetBuildShow";
    import { arrayLens, stringLens } from "../utils/commonLenses";
    import {
        TIME_INTERVAL_OPTIONS,
        TEST_CASES_TARGET_BUILDS_SUMMARY_TARGET_BUILDS_PER_PAGE,
        TEST_CASES_TARGET_BUILDS_SUMMARY_TEST_CASES_PER_PAGE,
        CI_SERVICES,
    } from "../utils/constants";
    import { valueToArray, formatDate, extractParams } from "../utils/helpers";
    import filtersMixin from "../utils/filtersMixin";
    import api from "../api/api";

    export default {
        name: "TestRunIndex",
        mixins: [filtersMixin],
        props: ["defaultFilterValues"],
        data() {
            return {
                isLoading: false,
                error: null,
                filters: {},
                TIME_INTERVAL_OPTIONS,
                PER_PAGE: TEST_CASES_TARGET_BUILDS_SUMMARY_TARGET_BUILDS_PER_PAGE,
                TEST_CASES_PER_PAGE: TEST_CASES_TARGET_BUILDS_SUMMARY_TEST_CASES_PER_PAGE,
                CI_SERVICES,
                showTestResultsDetails: false,
                showTargetBuildDetails: false,
                dataIsLoaded: false,

                queries: {
                    product: '',
                    platform: '',
                    branch: '',
                    target_name: '',
                    ciService: '',
                    mtrName: '',
                    testSuiteName: '',
                    buildType: '',
                    additionalTool: '',
                },

                failedTestCasesCountIsLoading: false,
                failedTestCasesCount: null,
                failedTestCases: null,
                failedTestCasesIsLoading: false,
                showFailedTestCases: false,
            }
        },
        computed: {
            ...mapGetters('TargetBuilds', [
                'targetBuilds',
                'testRunsTargetBuilds',
                'testRunsTargetBuildsTotalCount',
            ]),
            ...mapGetters('TestCases', [
                'testCasesTotalCount',
            ]),
            ...mapGetters('TestResults', [
                'testResults',
            ]),
            ...mapGetters('Auth', [
                'hasAccess',
            ]),
            currentTestResultsDetails() {
                const targetBuildId = this.$route.query.currentTargetBuildId;
                const testCaseId = this.$route.query.currentTestCaseId;
                if (targetBuildId == null || testCaseId == null) 
                    return null;

                const filtersFromQuery = this.getFiltersFromQuery(this.$route.query);
                const testAndRunFilters = extractParams(
                    filtersFromQuery, 
                    [
                        "platform_ids", 
                        "ci_service_ids", 
                        "build_type_ids", 
                        "mtr_ids",
                        "run_id",
                        "runs_with_additional_tools", 
                        "additional_tool_ids", 
                        "test_suite_name_ids", 
                        "time_interval", 
                        "dates",
                    ],
                );
                testAndRunFilters.dates = testAndRunFilters.dates.map(date => formatDate(date))
                return { targetBuildId, testCaseId, testAndRunFilters };
            },
            currentTargetBuildDetails() {
                const targetBuildId = this.$route.query.currentTargetBuildDetailsId;
                if (targetBuildId == null) return null;
                return targetBuildId;
            },
            currentPage() {
                return parseInt(this.$route.query.page || 1);
            },
            rowsCurrentPage() {
                return parseInt(this.$route.query.rowsPage || 1);
            },
            testCasesPage() {
                return this.testCases.slice(
                    (this.rowsCurrentPage - 1) * this.TEST_CASES_PER_PAGE,
                    this.rowsCurrentPage * this.TEST_CASES_PER_PAGE
                )
            },
            totalCasesPages() {
              return Math.ceil(this.testCases.length / this.TEST_CASES_PER_PAGE)
            },
        },
        methods: {
            ...mapActions('Platforms', [
                'fetchPlatforms',
            ]),
            ...mapActions('TargetBuilds', [
                'fetchBranches',
                'fetchProducts',
                'fetchBuildTypes',
                'fetchAdditionalTools',
                'fetchCIServices',
                'fetchByName'
            ]),
            ...mapMutations('TestCases', [
                'CLEAR_TEST_CASES',
            ]),
            ...mapMutations('TestResults', [
                'CLEAR_TEST_RESULTS',
            ]),
            ...mapMutations('TargetBuilds', [
                'CLEAR_TEST_RUNS_TARGET_BUILDS',
            ]),
            ...mapActions('TestResults', [
                'fetchTestResults',
            ]),
            ...mapActions('TestRuns', [
                'fetchMtrNames',
            ]),
            ...mapActions('TestSuites', [
                'fetchTestSuiteNames',
            ]),
            getFiltersFromQuery(query) {
                return {
                    platform_ids: valueToArray(query.platform_ids),
                    branch_ids: valueToArray(query.branch_ids),
                    mtr_ids: valueToArray(query.mtr_ids),
                    test_suite_name_ids: valueToArray(query.test_suite_name_ids),
                    product_ids: valueToArray(arrayLens.getValue(query, 'product_ids', ["2", "8"])),
                    show_skipped_results: query.show_skipped_results === 'true',
                    only_last_commits: query.only_last_commits === 'true',
                    time_interval: stringLens.getValue(query, 'time_interval', this.defaultFilterValues.timeInterval),
                    dates: valueToArray(query.dates).map(item => new Date(item)),
                    test_name: query.test_name || '',
                    commit_id: query.commit_id || '',
                    columnstore_commit_id: query.columnstore_commit_id || '',
                    target_name: query.target_name || this.defaultFilterValues.targetRegexp || '',
                    run_id: query.run_id || '',
                    ci_service_ids: valueToArray(query.ci_service_ids),
                    build_type_ids: valueToArray(query.build_type_ids),
                    additional_tool_ids: valueToArray(query.additional_tool_ids),
                    runs_with_additional_tools: query.runs_with_additional_tools === 'true',
                    excluded_target_build_ids: valueToArray(query.excluded_target_build_ids),
                };
            },
            setFiltersByQuery(query) {
                this.filters = this.getFiltersFromQuery(query);
            },
            nextCasesPage() {
              this.changeCasesPage(this.rowsCurrentPage + 1)
            },
            prevCasesPage() {
              this.changeCasesPage(this.rowsCurrentPage - 1)
            },
            fetchData() {
                return [
                    this.fetchPlatforms(),
                    this.fetchBranches(),
                    this.fetchMtrNames(),
                    this.fetchTestSuiteNames(),
                    this.fetchProducts(),
                    this.fetchBuildTypes(),
                    this.fetchAdditionalTools(),
                    this.fetchCIServices(),
                ];
            },
            async getTargetByName() {
                const targetName = this.$route.query.detail_target_name;
                if (targetName == null) return;
                delete this.$route.query.detail_target_name;
                const targetId = await this.fetchByName(targetName);
                const query = Object.assign({}, this.$route.query, {
                  currentTargetBuildDetailsId: targetId,
                });
                await this.$router.push({ name: this.$route.name, query });
            },
            async getBranchByName() {
                const branchName = this.$route.query.branches;
                if (!branchName)
                  return;

                const branch = this.branches.find((branch) => branch.name === branchName);
                if (branch) {
                  this.filters.branch_ids.push(`${branch.id}`);
                  await this.replaceRouterField(["branches"], "branch_ids", `${branch.id}`)
                } else {
                  return Promise.reject(`Unable to found branch ${branchName}`)
                }
            },
            async getProductByName() {
                const productName = this.$route.query.products;
                if (!productName)
                  return;

                if (productName === "all") {
                    this.filters.product_ids = [];
                    await this.replaceRouterField("products")
                    return;
                }

                const product = this.products.find((product) => product.name === productName);
                if (product) {
                  this.filters.product_ids = [`${product.id}`];
                  await this.replaceRouterField("products", "product_ids", [`${product.id}`])
                } else {
                  return Promise.reject(`Unable to found product ${productName}`)
                }
            },
            async getMtrByName() {
                const mtrName = this.$route.query.mtrs;
                if (!mtrName)
                  return;

                const mtr = this.mtrNames.find((mtr) => mtr.name === mtrName);
                if (mtr) {
                  this.filters.mtr_ids = [`${mtr.id}`];
                  await this.replaceRouterField(["mtrs"], "mtr_ids", [`${mtr.id}`])
                } else {
                  return Promise.reject(`Unable to found MTR ${mtrName}`)
                }
            },
            async getTestSuitesByName() {
                const testSuiteName = this.$route.query.test_suite_names;
                if (!testSuiteName)
                  return;

                const testSuite = this.testSuiteNames.find((testSuite) => testSuite.name === testSuiteName);
                if (testSuite) {
                    this.filters.test_suite_name_ids = [`${testSuite.id}`];
                    await this.replaceRouterField("test_suite_names",
                        "test_suite_name_ids", [`${testSuite.id}`])
                } else {
                    return Promise.reject(`Unable to found test suite ${testSuiteName}`)
                }
            },
            async getPlatformsByName() {
                const platformName = this.$route.query.platform_name;
                const platformVersion = this.$route.query.platform_version;
                const platformArchitecture = this.$route.query.platform_architecture;
                if (!platformName)
                    return;

                let platform
                if ((platformVersion) && (platformArchitecture))
                    platform = this.platforms.find((platform) => platform.platform === platformName &&
                        platform.version === platformVersion &&
                        platform.architecture === platformArchitecture);
                else
                    platform = this.platforms.find((platform) => platform.platform === platformName);
                if (platform) {
                    this.filters.platform_ids = [`${platform.id}`];
                    await this.replaceRouterField(
                        ["platform_name", "platform_version", "platform_architecture"],
                        "platform_ids", [`${platform.id}`]
                    )
                } else {
                    return Promise.reject(`Unable to found platform with name: ${platformName},
                    version: ${platformVersion}, architecture: ${platformArchitecture}`)
                }
            },
            async replaceRouterField(oldFields, newField, value) {
                const query = Object.assign({}, this.$route.query);
                for (let oldField of oldFields)
                    delete query[oldField];
                query[newField] = value;
                await this.$router.push({ name: this.$route.name, query });
            },
            async setFiltersByName() {
                await this.getBranchByName();
                await this.getProductByName();
                await this.getTargetByName();
                await this.getMtrByName();
                await this.getTestSuitesByName();
                await this.getPlatformsByName();
            },
            async search() {
                if (!this.hasAccess) return;
                this.error = null;
                this.isLoading = true;
                try {
                    const filters = Object.assign(
                        {}, 
                        this.filters,
                        {
                            page: this.currentPage,
                            per_page: this.PER_PAGE,
                            dates: this.filters.dates.map(date => formatDate(date)),
                        }
                    );
                    await this.fetchTestResults({ filters });
                    this.dataIsLoaded = true;
                } catch(message) {
                    this.error = message;
                }
                this.isLoading = false;
            },
            onSelectTestResult({ testCaseId, targetBuildId }) {
                const query = Object.assign({}, this.$route.query, {
                    currentTestCaseId: testCaseId,
                    currentTargetBuildId: targetBuildId,
                });
                this.$router.push({ name: this.$route.name, query });
            },
            onCloseTestResultsDetails() {
                const query = this._.omit(this.$route.query, ['currentTestCaseId', 'currentTargetBuildId']);
                this.$router.push({ name: this.$route.name, query });
            },
            onSelectTargetBuild(targetBuildId) {
                const query = Object.assign({}, this.$route.query, {
                    currentTargetBuildDetailsId: targetBuildId,
                });
                this.$router.push({ name: this.$route.name, query });
            },
            onCloseTargetBuild() {
                const query = this._.omit(this.$route.query, ['currentTargetBuildDetailsId']);
                this.$router.push({ name: this.$route.name, query });
            },
            async onChangePage(page) {
                const query = Object.assign({}, this.$route.query, { page, rowsPage: 1 });
                await this.$router.push({ name: this.$route.name, query });
                await this.search();
                this.$scrollTo('#tablePagination');
            },
            async changeCasesPage(rowsPage) {
                const query = Object.assign({}, this.$route.query, { rowsPage });
                await this.$router.push({ name: this.$route.name, query });
            },
            clearData() {
                this.CLEAR_TEST_RUNS_TARGET_BUILDS();
                this.CLEAR_TEST_CASES();
                this.CLEAR_TEST_RESULTS();
            },
            clearExcludedTargetBuilds() {
                this.filters.excluded_target_build_ids = [];
                this.search();
            },
            excludeTargetBuild(id) {
                this.filters.excluded_target_build_ids.push(id);
                this.$router.replace({
                    name: this.$route.name,
                    query: Object.assign({}, this.$route.query,
                        {excluded_target_build_ids: this.filters.excluded_target_build_ids})
                });
                this.search()
            },
            afterSearch() {
                this.$scrollTo('#tablePagination');
            },
            fetchJiraIssues(id) {
                return api.testResults.checkJiraIssues(id);
            },
            fetchJiraSummary(ids) {
                return api.testResults.fetchJiraSummary(ids);
            }
        },
        components: {
            TestCasesTable,
            TestResultsDetails,
            TargetBuildShow,
        },
    }
</script>

<style lang="scss">

</style>
